import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { ISODateString } from "../types/date";
import { FSMeetingAvailability, FSMeetingLocation, FSMeetingTemplate } from "../types/scheduling/fsmeetings";
import { OtherSchedule } from "../types/scheduling/other";
import { api, schedulingPath } from "./api";
import QueryKeys from "./queryKeys";

class FSMeetingsAPI {
  static base = schedulingPath("/fsmeetings");
  static locationPath = `${FSMeetingsAPI.base}/locations`;
  static schedulePath = `${FSMeetingsAPI.base}/schedule`;
  static availabilityPath = `${FSMeetingsAPI.base}/availabilities`;
  static templatePath = `${FSMeetingsAPI.base}/templates`;

  async autoFill(from: ISODateString, to: ISODateString, blank: boolean): Promise<OtherSchedule[]> {
    return api
      .post(`${FSMeetingsAPI.schedulePath}/${from}_${to}`, {
        searchParams: { blank: blank },
      })
      .json();
  }

  async clearSchedule(month: ISODateString) {
    await api.delete(`${FSMeetingsAPI.schedulePath}/clear/${month}`);
  }

  async getLocations(): Promise<FSMeetingLocation[]> {
    return api.get(FSMeetingsAPI.locationPath).json();
  }

  async setLocation(loc: FSMeetingLocation): Promise<FSMeetingLocation> {
    return api.put(FSMeetingsAPI.locationPath, { json: loc }).json();
  }

  async deleteLocation(loc: FSMeetingLocation) {
    await api.delete(`${FSMeetingsAPI.locationPath}/${loc.id}`);
  }

  async getAvailabilities(): Promise<FSMeetingAvailability[]> {
    return api.get(FSMeetingsAPI.availabilityPath).json();
  }

  async setAvailabilities(avails: FSMeetingAvailability[]): Promise<FSMeetingAvailability[]> {
    return api.put(FSMeetingsAPI.availabilityPath, { json: avails }).json();
  }

  async getTemplates(): Promise<FSMeetingTemplate[]> {
    return api.get(FSMeetingsAPI.templatePath).json();
  }

  async setTemplate(template: FSMeetingTemplate): Promise<FSMeetingTemplate> {
    return api.put(FSMeetingsAPI.templatePath, { json: template }).json();
  }

  async deleteTemplate(template: FSMeetingTemplate) {
    await api.delete(`${FSMeetingsAPI.templatePath}/${template.id}`);
  }
}

export function useFSMeetingLocations(options?: Partial<UseQueryOptions<FSMeetingLocation[], Error>>) {
  return useQuery({
    queryKey: [QueryKeys.FSMeetingLocations],
    queryFn: () => fsMeetingsAPI.getLocations(),
    ...options,
  });
}

export const fsMeetingsAPI = new FSMeetingsAPI();
